<script lang="ts">
  // Cookie banner (until the visitor has chosen) and the preferences dialog.
  // Mount once per layout; openConsentPreferences() reopens the dialog.
  import '../messages/legal';
  import { onMount } from 'svelte';
  import { fly } from 'svelte/transition';
  import { cubicOut } from 'svelte/easing';
  import { Cookie, Lock } from '@lucide/svelte';
  import Button from '../ui/Button.svelte';
  import Modal from '../ui/Modal.svelte';
  import Toggle from '../ui/Toggle.svelte';
  import { t } from '../i18n.svelte';
  import { initTracking } from '../tracking';
  import { consent } from './consent.svelte';

  let analytics = $state(false);
  let marketing = $state(false);
  let mounted = $state(false);

  onMount(() => {
    initTracking();
    // A short delay so the banner does not fight with the page's first paint.
    const timer = setTimeout(() => (mounted = true), 400);
    return () => clearTimeout(timer);
  });

  // Fill the switches with the current choice whenever the dialog opens.
  $effect(() => {
    if (!consent.preferencesOpen) return;
    analytics = consent.value?.analytics ?? false;
    marketing = consent.value?.marketing ?? false;
  });

  const showBanner = $derived(mounted && !consent.decided && !consent.preferencesOpen);
</script>

{#if showBanner}
  <div class="banner" role="region" aria-label={t('consent.title')} transition:fly={{ y: 16, duration: 220, easing: cubicOut }}>
    <div class="head">
      <span class="icon" aria-hidden="true"><Cookie size={18} /></span>
      <h2>{t('consent.title')}</h2>
    </div>
    <p>
      {t('consent.text')}
      <a href="/cookies">{t('consent.policy_link')}</a>
    </p>
    <div class="actions">
      <Button variant="ghost" size="sm" onclick={() => consent.openPreferences()}>{t('consent.preferences')}</Button>
      <div class="choices">
        <Button variant="secondary" block onclick={() => consent.rejectAll()}>{t('consent.reject')}</Button>
        <Button variant="primary" block onclick={() => consent.acceptAll()}>{t('consent.accept')}</Button>
      </div>
    </div>
  </div>
{/if}

<Modal bind:open={consent.preferencesOpen} title={t('consent.prefs_title')} description={t('consent.prefs_description')} size="md">
  <div class="stack-sm">
    <div class="option locked">
      <div class="option-text">
        <span class="option-title">{t('consent.necessary')} <span class="lock"><Lock size={12} aria-hidden="true" /></span></span>
        <span class="option-desc">{t('consent.necessary_desc')}</span>
      </div>
      <span class="always">{t('consent.always_on')}</span>
    </div>
    <div class="option">
      <Toggle bind:checked={analytics} label={t('consent.analytics')} description={t('consent.analytics_desc')} />
    </div>
    <div class="option">
      <Toggle bind:checked={marketing} label={t('consent.marketing')} description={t('consent.marketing_desc')} />
    </div>
  </div>
  <p class="more"><a href="/cookies" onclick={() => (consent.preferencesOpen = false)}>{t('consent.policy_link')}</a></p>

  {#snippet footer()}
    <Button variant="ghost" onclick={() => consent.rejectAll()}>{t('consent.reject_all')}</Button>
    <span class="spacer"></span>
    <Button variant="secondary" onclick={() => consent.choose({ analytics, marketing })}>{t('consent.save')}</Button>
    <Button variant="primary" onclick={() => consent.acceptAll()}>{t('consent.accept_all')}</Button>
  {/snippet}
</Modal>

<style>
  .banner {
    position: fixed;
    z-index: var(--z-drawer);
    left: var(--space-4);
    bottom: calc(var(--space-4) + env(safe-area-inset-bottom));
    width: min(440px, calc(100vw - 2 * var(--space-4)));
    padding: var(--space-4) var(--space-5);
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    background: var(--bg-elevated);
    border: 1px solid var(--border);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-lg);
  }
  .head { display: flex; align-items: center; gap: var(--space-2); }
  .icon {
    width: var(--control-h-sm); height: var(--control-h-sm); border-radius: var(--radius-sm);
    display: grid; place-items: center;
    background: var(--brand-soft); color: var(--brand);
    flex-shrink: 0;
  }
  h2 { font-size: var(--text-md); }
  p { font-size: var(--text-sm); color: var(--text-2); line-height: var(--leading-relaxed); }
  p a { white-space: nowrap; font-weight: var(--weight-medium); }
  .actions { display: flex; align-items: center; justify-content: space-between; gap: var(--space-2); flex-wrap: wrap; }
  .choices { display: grid; grid-auto-flow: column; gap: var(--space-2); }

  .option {
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: var(--space-3) var(--space-4);
    background: var(--surface);
  }
  .option.locked { display: flex; align-items: center; justify-content: space-between; gap: var(--space-4); background: var(--surface-2); }
  .option-text { display: flex; flex-direction: column; gap: var(--space-0_5); min-width: 0; }
  .option-title { display: inline-flex; align-items: center; gap: var(--space-1_5); font-size: var(--text-base); font-weight: var(--weight-semibold); color: var(--text); }
  .lock { display: inline-flex; color: var(--text-3); }
  .option-desc { font-size: var(--text-sm); color: var(--text-2); }
  .always { font-size: var(--text-xs); font-weight: var(--weight-semibold); color: var(--success); white-space: nowrap; }
  .more { margin-top: var(--space-3); font-size: var(--text-xs); }

  @media (max-width: 520px) {
    .banner { left: var(--space-2); right: var(--space-2); width: auto; bottom: calc(var(--space-2) + env(safe-area-inset-bottom)); }
    .choices { order: -1; flex: 1 0 100%; grid-template-columns: 1fr 1fr; }
  }
</style>
